/**
 * Owner: Masa Bayu
 * File: shell-primitives.ts
 * Date:Time: 2026-09-15 20:00
 * Description: QXRead shell component primitives bound to @qx/ui token paths.
 */

import {
  createQXComponentPrimitive,
  createQXComponentPrimitiveAccessibility,
  createQXComponentPrimitiveCatalog,
  createQXDesignTokensFromSystem,
  validateQXComponentPrimitivesAgainstSystem,
  type QXComponentPrimitiveCatalog,
  type QXDesignSystem,
} from '@qx/ui';

/**
 * Framework-neutral shell primitives — React chrome consumes these contracts.
 */
export function createQxreadShellPrimitiveCatalog(
  system: QXDesignSystem,
): QXComponentPrimitiveCatalog {
  const catalog = createQXComponentPrimitiveCatalog({
    primitives: [
      createQXComponentPrimitive({
        identity: { id: 'qxread-skip-link', name: 'Skip Link', family: 'action' },
        semanticRole: 'link',
        tokens: ['color.primary', 'typography.label', 'spacing.sm'],
        states: ['default', 'focus'],
        accessibility: createQXComponentPrimitiveAccessibility({
          a11yRole: 'link',
          labelKey: 'a11y.skip_to_content',
          focusable: true,
        }),
      }),
      createQXComponentPrimitive({
        identity: { id: 'qxread-nav-link', name: 'Nav Link', family: 'action' },
        semanticRole: 'link',
        tokens: ['color.foreground', 'color.primary', 'typography.label', 'spacing.sm'],
        states: ['default', 'hover', 'focus', 'selected'],
        accessibility: createQXComponentPrimitiveAccessibility({
          a11yRole: 'link',
          labelKey: 'a11y.primary_nav_item',
          focusable: true,
        }),
      }),
      createQXComponentPrimitive({
        identity: { id: 'qxread-brand-display', name: 'Brand Display', family: 'text' },
        semanticRole: 'display',
        tokens: ['typography.display', 'color.primary'],
        states: ['default'],
        accessibility: createQXComponentPrimitiveAccessibility({
          a11yRole: 'text',
          labelKey: 'a11y.brand_name',
          focusable: false,
        }),
      }),
      createQXComponentPrimitive({
        identity: { id: 'qxread-route-panel', name: 'Route Panel', family: 'surface' },
        semanticRole: 'panel',
        tokens: ['color.surface', 'color.border', 'spacing.lg', 'elevation.low', 'shape.large'],
        states: ['default'],
        accessibility: createQXComponentPrimitiveAccessibility({
          a11yRole: 'region',
          labelKey: 'a11y.route_panel',
          focusable: false,
        }),
      }),
      createQXComponentPrimitive({
        identity: { id: 'qxread-shell-section', name: 'Shell Section', family: 'container' },
        semanticRole: 'section',
        tokens: ['spacing.md', 'color.background'],
        states: ['default'],
        accessibility: createQXComponentPrimitiveAccessibility({
          a11yRole: 'region',
          labelKey: 'a11y.shell_section',
          focusable: false,
        }),
      }),
    ],
  });
  validateQXComponentPrimitivesAgainstSystem(catalog, system);
  createQXDesignTokensFromSystem(system);
  return catalog;
}
